import React, { useState } from 'react';
import { Button, TextField } from '@material-ui/core';
import Link from '@material-ui/core/Link';
import { useHistory, useLocation } from 'react-router-dom';
import { post } from '../../utils/ajax';
import { remoteRoutes, localRoutes } from '../../data/constants';
import Toast from '../../utils/Toast';
import { useLoginStyles } from './loginStyles';
import AuthLayout from './AuthLayout';
import { colors } from '../../theme/tokens';

const CORAL = colors.brand;

/**
 * Reached right after a plain (non-Google) signup, before login — a code
 * was already emailed to them by the register endpoint. Google signups
 * skip this screen entirely since Google already proved the address.
 */
const VerifyEmail = () => {
  const classes = useLoginStyles();
  const history = useHistory();
  const location = useLocation();
  // HashRouter can't carry router state (it's silently dropped), so the
  // email rides along as a query param instead — see RegisterForm.tsx.
  const emailFromQuery = new URLSearchParams(location.search).get('email');
  const [email, setEmail] = useState(emailFromQuery ?? '');
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const resend = () => {
    if (!email.trim()) {
      Toast.error('Enter your email first');
      return;
    }
    setSending(true);
    post(
      remoteRoutes.emailVerificationSendPublic,
      { email: email.trim() },
      () => Toast.info('We have sent a code to your email address'),
      (err: any) =>
        Toast.error(
          err?.response?.body?.message || 'Could not send the code just now',
        ),
      () => setSending(false),
    );
  };

  const confirm = () => {
    if (!email.trim()) {
      Toast.error('Enter your email first');
      return;
    }
    if (code.length < 6) {
      Toast.error('Enter the 6-digit code');
      return;
    }
    setConfirming(true);
    post(
      remoteRoutes.emailVerificationConfirmPublic,
      { email: email.trim(), code },
      () => {
        Toast.success('Email confirmed — you can now sign in.');
        history.push(localRoutes.login);
      },
      (err: any) =>
        Toast.error(err?.response?.body?.message || "That code isn't right"),
      () => setConfirming(false),
    );
  };

  return (
    <AuthLayout
      title="Verify your email"
      subtitle="Enter the code we sent you to finish creating your account."
    >
      <div className={classes.form}>
        {!emailFromQuery && (
          <TextField
            fullWidth
            label="Email address"
            variant="outlined"
            size="small"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ marginBottom: 16 }}
          />
        )}
        {emailFromQuery && (
          <div
            style={{
              fontSize: 13,
              color: colors.textMuted,
              marginBottom: 16,
            }}
          >
            Code sent to <strong>{email}</strong>
          </div>
        )}
        <TextField
          fullWidth
          label="6-digit code"
          variant="outlined"
          size="small"
          value={code}
          inputProps={{ inputMode: 'numeric' }}
          onChange={(e) =>
            setCode(String(e.target.value).replace(/\D/g, '').slice(0, 6))
          }
          style={{ marginBottom: 16 }}
        />
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={confirming || code.length < 6}
          onClick={confirm}
          className={classes.submit}
        >
          {confirming ? 'Checking…' : 'Confirm'}
        </Button>
        <Button
          fullWidth
          disabled={sending}
          onClick={resend}
          style={{
            textTransform: 'none',
            marginTop: 10,
            color: CORAL,
            fontWeight: 600,
          }}
        >
          {sending ? 'Sending…' : "Didn't get a code? Resend"}
        </Button>
      </div>

      <div className={classes.footerText}>
        <Link
          className={`${classes.link} ${classes.linkAccent}`}
          onClick={() => history.push(localRoutes.login)}
        >
          Back to login
        </Link>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmail;
